import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import flagEn from "../assets/images/EN.svg";
import {
  DEFAULT_LOCALE,
  LANGUAGES,
  STORAGE_KEY,
  getLanguage,
  getLocaleFromPath,
  getTranslator,
  withLocalePath,
} from "../i18n";

const LANGUAGE_FLAGS = {
  EN: flagEn,
  AR: "https://flagcdn.com/w40/sa.png",
  TR: "https://flagcdn.com/w40/tr.png",
};

const LanguageContext = createContext(null);

const readStoredLocale = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
};

const readInitialLocale = () => {
  if (typeof window !== "undefined") {
    const fromPath = getLocaleFromPath(window.location.pathname);
    if (fromPath) return fromPath;
  }
  return readStoredLocale();
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathLocale = getLocaleFromPath(location.pathname);
  const [locale, setLocale] = useState(readInitialLocale);
  const language = getLanguage(locale);
  const t = useMemo(() => getTranslator(language.locale), [language.locale]);

  useEffect(() => {
    if (pathLocale) {
      setLocale(pathLocale);
    }
  }, [pathLocale]);

  useEffect(() => {
    document.documentElement.lang = language.htmlLang;
    document.documentElement.dir = language.dir;

    try {
      localStorage.setItem(STORAGE_KEY, language.locale);
    } catch {
      // Ignore private-mode storage failures.
    }
  }, [language]);

  const setLanguage = useCallback(
    (next) => {
      const nextLocale = getLanguage(next).locale;
      setLocale(nextLocale);
      const nextPath = withLocalePath(
        nextLocale,
        location.pathname,
        location.search,
        location.hash
      );
      if (nextPath !== `${location.pathname}${location.search}${location.hash}`) {
        navigate(nextPath);
      }
    },
    [location.hash, location.pathname, location.search, navigate]
  );

  const value = useMemo(
    () => ({
      locale: language.locale,
      language: {
        ...language,
        flag: LANGUAGE_FLAGS[language.code],
      },
      languages: LANGUAGES.map((item) => ({
        ...item,
        flag: LANGUAGE_FLAGS[item.code],
      })),
      setLanguage,
      t,
    }),
    [language, setLanguage, t]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
